import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { nameSchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  CheckboxField,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormRootError,
  InputField,
} from '@blimo/ui';

const folderSchema = nameSchema.extend({ visible_in_portal: z.boolean() });

/**
 * Ordner anlegen (Name, im Portal sichtbar) bzw. umbenennen. `onSave` wirft bei Fehlern (ProblemDetails → Formular).
 * `defaultVisible`: Vorbelegung „Im Portal sichtbar“ für neue Ordner (Mietverhältnis-Ordner: intern).
 * @param {{ folder?: any, defaultVisible?: boolean, onSave: (values: { name: string, visible_in_portal: boolean }) => Promise<void>, onDone: () => void }} props
 */
export function FolderForm({ folder, defaultVisible = true, onSave, onDone }) {
  const form = useForm({
    resolver: zodResolver(folderSchema),
    defaultValues: { name: folder?.name ?? '', visible_in_portal: folder ? Boolean(folder.visible_in_portal) : defaultVisible },
  });
  const submit = form.handleSubmit(async (values) => {
    try {
      await onSave({ name: values.name.trim(), visible_in_portal: values.visible_in_portal });
      onDone();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['name', 'visible_in_portal'] });
    }
  });
  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{folder ? 'Ordner umbenennen' : 'Ordner anlegen'}</DialogTitle>
          <DialogDescription>
            {folder ? `Neuer Name für „${folder.name}“.` : 'Ordner gliedern die Dokumente – in der Verwaltung und im Portal.'}
          </DialogDescription>
        </DialogHeader>
        <FormRootError />
        <InputField name="name" label="Name des Ordners" autoComplete="off" />
        {!folder && <CheckboxField name="visible_in_portal" label="Im Portal sichtbar" />}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {folder ? 'Speichern' : 'Anlegen'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
